import React, { useRef, useEffect, useCallback } from "react";
import { Container } from "./ui/Container";
import { Footer } from "./Footer";
import { CommonQuestions } from "./CommonQuestions";
import { Button } from "./ui/Button";
import { trackTryKnoothClick } from "../lib/analytics";

/** Play a <video> when it scrolls into view, pause when it leaves. */
function useAutoplayOnScroll() {
  const refs = useRef([]);

  const setRef = useCallback((index) => (el) => {
    refs.current[index] = el;
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    const currentRefs = refs.current.filter(Boolean);
    currentRefs.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return setRef;
}
import knoothIconLarge from "../assets/Knooth_icon_large.png";
import knoothIconLargeWebP from "../assets/Knooth_icon_large.webp";
import precisionControlsImg from "../assets/precision_controls.png";
import precisionControlsWebP from "../assets/precision_controls.webp";
import captureClarityImg from "../assets/capture_clarity.png";
import layerMeaningImg from "../assets/layer_meaning.png";
import layerMeaningWebP from "../assets/layer_meaning.webp";
import designedToGuideImg from "../assets/designed_to_guide.png";
import designedToGuideWebP from "../assets/designed_to_guide.webp";
import shapeTheStoryVideo from "../assets/shape_the_story.mp4";
import autoZoomVideo from "../assets/auto-zoom.mp4";
import readyForEveryImg from "../assets/ready_for_every.png";
import readyForEveryWebP from "../assets/ready_for_every.webp";
import privacyByDesignImg from "../assets/privacy_by_design.png";
import privacyByDesignWebP from "../assets/privacy_by_design.webp";
import refineYourVoiceVideo from "../assets/refine_your_voice.mp4";
import captionsVideo from "../assets/Captions.mp4";
import fineTune from "../assets/fine-tuning.png";
import fineTuneWebP from "../assets/fine-tuning.webp";
import backgroundMasking from "../assets/background-masking.png";
import backgroundMaskingWebP from "../assets/background-masking.webp";
import studioReadyAudio from "../assets/studio-audio.png";
import studioReadyAudioWebP from "../assets/studio-audio.webp";

const Features = () => {
  const videoRef = useAutoplayOnScroll();

  return (
    <section
      id="features"
      className="relative isolate z-[60] -mt-20 pt-20 bg-[#FBFBFD] rounded-t-[20px] md:rounded-t-[60px] transform-gpu"
    >
      <Container>
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className="text-5xl md:text-6xl lg:text-[72px] font-[900] tracking-[-0.02em] mb-6 leading-none text-transparent bg-clip-text"
            style={{ backgroundImage: "linear-gradient(180deg, #31220D 57%, #666666 100%)" }}
          >
            More than recording.<br />It's a full canvas.
          </h2>
          <p className="text-lg md:text-xl text-[#666666] max-w-[800px] mx-auto leading-[1.5]">
            Record your screen, then refine it. With built-in editing, timelines, animations, and media layering, knooth lets you create professional videos without leaving the app.
          </p>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Card 1: Precision Controls */}
          <div
            className="rounded-3xl p-10 pb-0 border border-black/10 overflow-hidden flex flex-col lg:row-span-2"
            style={{ background: "linear-gradient(180deg, #FFECEC 0%, #EFEBFF 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Precision controls, when you need them
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-4 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Fine-tune layout, position, typography, color, audio, and export settings — all from a single, focused panel.
            </p>
            <div className="flex-grow flex items-end mt-auto">
              <picture>
                <source srcSet={precisionControlsWebP} type="image/webp" />
                <img
                  src={precisionControlsImg}
                  alt="Precision controls interface"
                  width={840}
                  height={2400}
                  loading="lazy"
                  className="w-full h-auto shadow-xl"
                />
              </picture>
            </div>
          </div>

          {/* Card 2: Capture with Clarity - spans 2 columns on lg */}
          <div
            className="rounded-3xl pt-10 pl-10 pb-10 pr-0 shadow-[0_2px_20px_rgba(0,0,0,0.04)] border border-black/5 lg:col-span-2 relative overflow-hidden"
            style={{ background: "linear-gradient(180deg, #DBFFD2 0%, #D5F4EC 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Capture with clarity
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-[48px] text-transparent bg-clip-text pr-10"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Capture exactly what you need, from full screens to fine details — even just yourself — with audio and visual precision from the start.
            </p>
            <div className="mt-auto rounded-tl-2xl overflow-hidden">
              <img
                src={captureClarityImg}
                alt="Capture with clarity interface"
                width={1558}
                height={168}
                loading="lazy"
                className="w-full h-auto shadow-xl"
              />
            </div>
          </div>

          {/* Card 3: Layer Meaning */}
          <div
            className="rounded-3xl p-10 pb-0 border border-black/10 overflow-hidden flex flex-col"
            style={{ background: "linear-gradient(180deg, #FFE9E9 0%, #FFF4E9 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Layer meaning onto your recordings
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-8 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Add text, images, audio, and vector shapes to your videos. Annotate key moments and guide attention exactly where it matters.
            </p>
            <div className="bg-transparent">
              <picture>
                <source srcSet={layerMeaningWebP} type="image/webp" />
                <img
                  src={layerMeaningImg}
                  alt="Layer meaning interface"
                  width={978}
                  height={948}
                  loading="lazy"
                  className="w-full h-auto rounded-xl"
                />
              </picture>
            </div>
          </div>

          {/* Card 4: Designed to Guide */}
          <div
            className="rounded-3xl p-10 border border-black/10 overflow-hidden"
            style={{ background: "linear-gradient(180deg, #EDE4FF 0%, #EED0EC 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Designed to guide attention
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-4 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Customize your cursor after recording — adjust size, add click sounds, and guide attention exactly where it matters.
            </p>
            <div className="bg-transparent">
              <picture>
                <source srcSet={designedToGuideWebP} type="image/webp" />
                <img
                  src={designedToGuideImg}
                  alt="Designed to guide interface"
                  width={834}
                  height={980}
                  loading="lazy"
                  className="w-full h-auto shadow-xl rounded-xl"
                />
              </picture>
            </div>
          </div>
        </div>

        {/* Shape the Story Card - Full Width */}
        <div
          className="rounded-3xl pt-10 pl-10 pb-0 pr-0 mt-6 overflow-hidden border border-gray-300"
          style={{ background: "linear-gradient(180deg, #181818 0%, #6F7186 100%)" }}
        >
          <h3
            className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
            style={{ backgroundImage: "linear-gradient(180deg, #E7E7E7 57%, #AAAAAA 100%)" }}
          >
            Shape the story over time
          </h3>
          <p
            className="text-[16px] font-normal leading-[140%] mb-8 pr-10 text-transparent bg-clip-text"
            style={{ backgroundImage: "linear-gradient(180deg, #E7E7E7 57%, #AAAAAA 100%)" }}
          >
            Trim clips, split layers, and animate elements to highlight key moments and keep attention exactly where you want it.
          </p>
          <div className="mt-auto">
            <video
              ref={videoRef(1)}
              src={shapeTheStoryVideo}
              width={1160}
              height={400}
              loop
              muted
              playsInline
              preload="metadata"
              className="w-full h-auto"
            />
          </div>
        </div>

        {/* Second Grid - Auto Zoom, Ready for Every Screen, Privacy by Design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {/* Card 1: Auto Zoom Cursor - spans 2 rows */}
          <div
            className="rounded-3xl pt-10 pl-10 pb-0 pr-0 border border-black/10 overflow-hidden flex flex-col lg:row-span-2 lg:col-span-2 relative"
            style={{ background: "linear-gradient(180deg, #FFECEC 0%, #EFEBFF 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Auto Zoom Cursor
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-4 text-transparent bg-clip-text z-10 relative"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Turn it on to automatically zoom into cursor actions, then fine-tune the generated keyframes anytime. You're always in control.
            </p>
            <div className="mt-auto lg:absolute lg:bottom-0 lg:right-0 lg:left-10">
              <video
                ref={videoRef(2)}
                src={autoZoomVideo}
                width={784}
                height={828}
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-auto rounded-tl-xl"
              />
            </div>
          </div>

          {/* Card 2: Ready for Every Screen */}
          <div
            className="rounded-3xl p-10 pb-0 border border-black/10 overflow-hidden flex flex-col"
            style={{ background: "linear-gradient(180deg, #DBFFD2 0%, #D5F4EC 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Ready for every screen
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-4 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Export your video in multiple aspect ratios, from widescreen to vertical and square.
            </p>
            <div className="flex-grow flex items-end mt-auto">
              <picture>
                <source srcSet={readyForEveryWebP} type="image/webp" />
                <img
                  src={readyForEveryImg}
                  alt="Video aspect ratio selector"
                  width={840}
                  height={822}
                  loading="lazy"
                  className="w-full h-auto"
                />
              </picture>
            </div>
          </div>

          {/* Card 3: Privacy by Design */}
          <div
            className="rounded-3xl p-10 border border-black/10 overflow-hidden flex flex-col"
            style={{ background: "linear-gradient(180deg, #F3E3FE 0%, #EDF1D9 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Privacy by design
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-4 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Your recordings never leave your device unless you choose to move them. knooth stores everything locally, giving you complete ownership and control.
            </p>
            <div className="flex-grow flex items-end justify-center mt-auto -mb-0">
              <picture>
                <source srcSet={privacyByDesignWebP} type="image/webp" />
                <img
                  src={privacyByDesignImg}
                  alt="Privacy by design"
                  width={747}
                  height={568}
                  loading="lazy"
                  className="w-auto h-auto max-w-full"
                />
              </picture>
            </div>
          </div>
        </div>

        {/* Third Grid - Refine your voice, Captions made simple */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Card 1: Refine your voice */}
          <div
            className="rounded-3xl p-10 pb-0 border border-black/10 overflow-hidden flex flex-col"
            style={{ background: "linear-gradient(180deg, #E3FFEB 0%, #F2F4FF 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Refine your voice
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-8 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Remove filler words or selected phrases using intelligence built into knooth — keeping your video natural, polished, and private.
            </p>
            <div className="flex-grow flex items-end mt-auto">
              <video
                ref={videoRef(3)}
                src={refineYourVoiceVideo}
                width={504}
                height={390}
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-auto rounded-t-2xl border-t border-l border-r border-black/10"
              />
            </div>
          </div>

          {/* Card 2: Captions made simple */}
          <div
            className="rounded-3xl p-10 pb-0 border border-black/10 overflow-hidden flex flex-col"
            style={{ background: "linear-gradient(180deg, #FFEBEB 0%, #F4F0FF 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Captions made simple
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-8 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Create captions with built-in intelligence, processed locally without relying on external services.
            </p>
            <div className="flex-grow flex items-end mt-auto">
              <video
                ref={videoRef(4)}
                src={captionsVideo}
                width={1010}
                height={782}
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-auto rounded-t-2xl border-t border-l border-r border-black/10"
              />
            </div>
          </div>
          
        </div>

        {/* Fine-tune animations */}
         <div
          className="rounded-3xl pt-10 pl-10 pb-0 pr-0 mt-6 overflow-hidden border border-gray-300"
          style={{ background: "linear-gradient(180deg, #181818 0%, #6F7186 100%)" }}
        >
          <h3
            className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
            style={{ backgroundImage: "linear-gradient(180deg, #E7E7E7 57%, #AAAAAA 100%)" }}
          >
            Fine-tune animations
          </h3>
          <p
            className="text-[16px] font-normal leading-[140%] mb-8 pr-10 text-transparent bg-clip-text"
            style={{ backgroundImage: "linear-gradient(180deg, #E7E7E7 57%, #AAAAAA 100%)" }}
          >
          Adjust easing curves with a full-featured graph editor.
          Get precise control over timing, speed, and motion.          
          </p>
          <div className="mt-auto">
            <picture>
              <source srcSet={fineTuneWebP} type="image/webp" />
              <img
                src={fineTune}
                alt="Graph editor interface"
                width={2320}
                height={796}
                loading="lazy"
                className="w-full h-auto"
              />
            </picture>
          </div>
        </div>

        {/* Background masking */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Card 1: Background masking */}
          <div
            className="rounded-3xl p-10 pb-0 border border-black/10 overflow-hidden flex flex-col"
            style={{ background: "linear-gradient(180deg, #F5FFE4 0%, #EED0EC 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Background masking
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-8 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Remove or blur your background after recording.
              Clean up distractions in a single click.
            </p>
            <div className="flex-grow flex items-end mt-auto">
              <picture>
                <source srcSet={backgroundMaskingWebP} type="image/webp" />
                <img
                  src={backgroundMasking}
                  alt="Background masking interface"
                  width={1010}
                  height={782}
                  loading="lazy"
                  className="w-full h-auto rounded-t-2xl border-t border-l border-r border-black/10"
                />
              </picture>
            </div>
          </div>
          {/* Studio-ready audio */}
          <div
            className="rounded-3xl p-10 pb-0 border border-black/10 overflow-hidden flex flex-col"
            style={{ background: "linear-gradient(180deg, #FFECEC 0%, #EFEBFF 100%)" }}
          >
            <h3
              className="text-[24px] font-[800] leading-[110%] mb-2 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Studio-ready audio
            </h3>
            <p
              className="text-[16px] font-normal leading-[140%] mb-8 text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #412F14 57%, #666666 100%)" }}
            >
              Reduce background noise for a cleaner, clearer sound.
              Fine-tune frequencies using the built-in equaliser.
            </p>
            <div className="flex-grow flex items-end mt-auto">
              <picture>
                <source srcSet={studioReadyAudioWebP} type="image/webp" />
                <img
                  src={studioReadyAudio}
                  alt="Studio-ready audio interface"
                  width={1010}
                  height={782}
                  loading="lazy"
                  className="w-full h-auto rounded-t-2xl border-t border-l border-r border-black/10"
                />
              </picture>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-[100px] h-px bg-black/10 w-full"></div>

        {/* Pricing Section */}
        <div id="pricing" className="pt-[100px]">
          {/* Section Header */}
          <div className="text-center mb-[80px]">
            <h2
              className="text-5xl md:text-6xl lg:text-[72px] font-[900] tracking-[-0.02em] mb-[24px] md:mb-[40px] leading-none text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #31220D 57%, #666666 100%)" }}
            >
              Simple pricing,<br />no surprises.
            </h2>
            <p className="text-[24px] md:text-[32px] font-normal text-[#666666] leading-[140%]">
              One license. All your devices.
            </p>
            <p className="text-[12px] md:text-[16px] font-normal text-[#666666] leading-[140%] max-w-[600px] mx-auto">
              No feature gating. No data collection. Files stay on your device.<br />
              1 week free for new subscribers.
            </p>
            <div className="mt-[32px] flex justify-center">
              <a
                href="https://apps.apple.com/app/knooth/id6756659300?mt=12"
                target="_blank"
                rel="noreferrer"
                data-analytics="try-knooth"
                data-location="pricing-cta"
                onClick={() => trackTryKnoothClick("pricing-cta")}
                className="w-[176px] h-[44px] px-6 py-[10px] rounded-full text-white font-bold text-[24px] hover:shadow-lg transition-all border-none leading-none flex items-center justify-center"
                style={{ background: "linear-gradient(180deg, #2F00EC 57%, #C8A5FF 100%)" }}
              >
                Try knooth
              </a>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="lg:-mx-12">
            <div className="flex flex-col md:flex-row md:flex-nowrap gap-4 items-start md:overflow-x-auto md:justify-center">
              {/* Free Trial Card */}
              <div className="bg-white rounded-[20px] p-[20px] border border-black/10 flex flex-col w-full md:w-[328px] md:flex-none">
                <span className="text-[16px] font-normal leading-[100%] text-[#201441] bg-white border border-black/10 rounded px-[6px] py-[4px] w-fit mb-[8px]">Free Trial</span>
                <h3 className="text-[32px] font-normal leading-[100%] text-[#412F14] mb-[2px]">Get Started</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-[32px] font-normal leading-[100%] text-[#2B00FF]">Free</span>
                </div>
                <p className="mt-[4px] text-[16px] font-normal leading-[100%] text-[#412F14]/70 mb-0">1 week free trial</p>
                <p className="mt-[48px] text-[16px] font-normal leading-[100%] text-[#412F14] mb-[16px]">Full access for 7 days.</p>
                <div className="mt-[16px] h-px bg-black/10 w-full" />
                <ul className="mt-[16px] space-y-4">
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>All recording modes</li>
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Full editing tools</li>
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Auto Zoom Cursor, AI features, etc</li>
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Local-only & privacy-first</li>
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Use on all Macs (same Apple ID)</li>
                </ul>
              </div>

              {/* Explorer Card */}
              <div className="bg-white rounded-[20px] p-[20px] border border-black/10 flex flex-col w-full md:w-[328px] md:flex-none">
                <span className="text-[16px] font-normal leading-[100%] text-[#412F14] bg-white border border-black/10 rounded px-[6px] py-[4px] w-fit mb-[8px]">Monthly</span>
                <h3 className="text-[32px] font-normal leading-[100%] text-[#412F14] mb-[2px]">Explorer</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-[32px] font-normal leading-[100%] text-[#2B00FF]">$9.99</span>
                  <span className="text-[20px] font-normal leading-[100%] text-[#2B00FF]">/month</span>
                </div>
                <p className="mt-[4px] text-[16px] font-normal leading-[100%] text-[#412F14]/70 mb-0">Pay as you go. Cancel anytime.</p>
                <p className="mt-[48px] text-[16px] font-normal leading-[100%] text-[#412F14] mb-[16px]">Explore at your own pace.</p>
                <div className="mt-[16px] h-px bg-black/10 w-full" />
                <ul className="mt-[16px] space-y-4">
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Everything in Free Trial</li>
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Monthly billing</li>
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Ongoing updates</li>
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Use on all Macs (same Apple ID)</li>
                </ul>
              </div>

              {/* Professional Card */}
              <div className="relative bg-white rounded-[20px] p-[20px] border-3 border-[#2B00FF]/40 flex flex-col w-full md:w-[328px] md:flex-none">
                <span className="text-[16px] font-normal leading-[100%] text-[#412F14] bg-white border border-black/10 rounded px-[6px] py-[4px] w-fit mb-[8px]">Yearly</span>
                <h3 className="text-[32px] font-normal leading-[100%] text-[#412F14] mb-[2px]">Professional</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-[32px] font-normal leading-[100%] text-[#2B00FF]">$59.99</span>
                  <span className="text-[20px] font-normal leading-[100%] text-[#2B00FF]">/year</span>
                </div>
                <div className="mt-1 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70">
                  $4.99/month&nbsp;•&nbsp;50% savings
                </div>
                {/* <p className="mt-[4px] text-[16px] font-normal leading-[100%] text-[#412F14]/70 mb-0">Available after Beta</p> */}
                <p className="mt-[48px] text-[16px] font-normal leading-[100%] text-[#412F14] mb-[16px]">Built for everyday work.</p>
                <div className="mt-[16px] h-px bg-black/10 w-full" />
                <ul className="mt-[16px] space-y-4">
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Everything in Monthly</li>
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Best value</li>
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>One year of updates</li>
                  <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[100%] text-[#412F14]/70"><span className="flex-shrink-0 text-[#412F14]/50"><svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" /></svg></span>Use on all Macs (same Apple ID)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="mt-[100px] h-px bg-black/10 w-full"></div>

          <CommonQuestions />

          {/* CTA Section */}
          <div className="mt-[270px] text-center pb-[280px]">
            <picture>
              <source srcSet={knoothIconLargeWebP} type="image/webp" />
              <img
                src={knoothIconLarge}
                alt="knooth icon"
                width={336}
                height={336}
                loading="lazy"
                className="w-[96px] h-[96px] mx-auto mb-[40px]"
              />
            </picture>
            <h2
              className="text-5xl md:text-6xl lg:text-[72px] font-[900] tracking-[-0.02em] leading-none text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #31220D 57%, #666666 100%)" }}
            >
              Turn screen recordings
              <br />
              into clear stories
            </h2>
            <p className="mt-[32px] text-xl md:text-[32px] font-normal leading-[140%] tracking-normal text-[#666666] max-w-[800px] mx-auto">
              knooth helps you capture, refine, and export professional videos — without switching tools or workflows.
            </p>
            <div className="mt-[40px] flex flex-col items-center gap-[10px]">
              <Button
                href="https://apps.apple.com/app/knooth/id6756659300?mt=12"
                target="_blank"
                rel="noreferrer"
                data-analytics="try-knooth"
                data-location="features-cta"
                onClick={() => trackTryKnoothClick("features-cta")}
                className="w-[176px] h-[44px] px-6 py-[10px] rounded-full text-white font-bold text-[24px] hover:shadow-lg transition-all border-none leading-none flex items-center justify-center"
                style={{ background: "linear-gradient(180deg, #2F00EC 57%, #C8A5FF 100%)" }}
              >
                Try knooth
              </Button>
              <p className="text-[14px] font-normal leading-[1.4] text-[#31220D]">1 week free for new subscribers.</p>
            </div>
          </div>
        </div>
      </Container>
      <Footer />
    </section>
  );
};

export { Features };
